import React from 'react';
import { createPortal } from 'react-dom';
import { Phone, PhoneOff, PhoneForwarded } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import formatPhone from '@/utils/formatPhoneForUser';
import type { Call } from '@twilio/voice-sdk';
import type { TwilioCallWithParams } from '@/types';
import { keepPageDialogsOpen } from './useCompactCallUi';

interface IncomingCallDialogProps {
	incomingCall: Call | null;
	onAccept: () => void;
	onDecline: () => void;
	customerName?: string;
	/** Accept was pressed and is waiting on the microphone check. */
	isAccepting?: boolean;
	/**
	 * Phones and short touch screens: a full-width banner at the top of the
	 * screen with large Accept and Decline buttons instead of the corner card.
	 */
	compact?: boolean;
}

const IncomingCallDialog: React.FC<IncomingCallDialogProps> = ({
	incomingCall,
	onAccept,
	onDecline,
	customerName,
	isAccepting = false,
	compact = false,
}) => {
	if (!incomingCall) return null;

	const callerNumber =
		(incomingCall as unknown as { parameters?: { From?: string } })
			?.parameters?.From || 'Unknown Number';

	const customParams = (incomingCall as TwilioCallWithParams)?.customParameters;
	const forwardedFromName = customParams?.get('forwardedFromName') || null;
	// What the caller chose in the phone menu, e.g. "Sales".
	const menuSelection = customParams?.get('menuSelection') || null;

	const getInitials = (name: string): string => {
		const parts = name.trim().split(/\s+/);
		if (parts.length >= 2) {
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		}
		return name.slice(0, 2).toUpperCase();
	};

	const callerInitials = customerName
		? getInitials(customerName)
		: getInitials(callerNumber);

	if (compact) {
		return createPortal(
			<div
				onPointerDown={keepPageDialogsOpen}
				className={cn(
					'pointer-events-auto fixed inset-x-0 top-0 z-[60] pl-[max(0.5rem,var(--safe-left))] pr-[max(0.5rem,var(--safe-right))] pt-[calc(var(--safe-top)+0.5rem)]',
					'duration-300 animate-in fade-in-0 slide-in-from-top-4 motion-reduce:animate-none',
				)}
			>
				<div
					role="alert"
					aria-live="assertive"
					className="mx-auto flex max-w-md flex-col gap-3 rounded-[28px] bg-gray-900/95 p-3 text-white shadow-2xl shadow-black/40 ring-1 ring-white/10 backdrop-blur-xl landscape:max-w-2xl landscape:flex-row landscape:items-center"
				>
					<div className="flex min-w-0 flex-1 items-center gap-3">
						<div className="relative shrink-0">
							<div className="flex size-12 items-center justify-center rounded-full border-2 border-blue-600 bg-blue-800">
								<span className="text-base font-bold">{callerInitials}</span>
							</div>
							{forwardedFromName && (
								<div className="absolute -bottom-1 -right-1 flex size-6 items-center justify-center rounded-full border-2 border-gray-900 bg-purple-600">
									<PhoneForwarded aria-hidden className="size-3" />
								</div>
							)}
						</div>
						<div className="min-w-0 flex-1">
							<p className={cn('truncate text-[13px] leading-tight', forwardedFromName ? 'text-purple-300' : 'text-gray-400')}>
								{forwardedFromName ? `Forwarded from ${forwardedFromName}` : 'Incoming call'}
							</p>
							<h2 className="truncate text-[17px] font-semibold leading-snug">
								{customerName || formatPhone(callerNumber)}
							</h2>
							{(customerName || menuSelection) && (
								<p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-tight text-gray-300">
									{customerName && <span className="truncate">{formatPhone(callerNumber)}</span>}
									{menuSelection && (
										<span className="shrink-0 rounded-full bg-blue-800/70 px-2 py-0.5 text-xs font-medium text-blue-100">
											Chose {menuSelection}
										</span>
									)}
								</p>
							)}
						</div>
					</div>

					<div className="grid grid-cols-2 gap-3 landscape:flex landscape:shrink-0">
						<button
							type="button"
							onClick={onDecline}
							aria-label="Decline call"
							className="flex h-14 items-center justify-center gap-2 rounded-full bg-red-600 px-5 text-[17px] font-semibold outline-none active:bg-red-700 focus-visible:ring-2 focus-visible:ring-white landscape:h-12"
						>
							<PhoneOff aria-hidden className="size-5" />
							Decline
						</button>
						<button
							type="button"
							onClick={onAccept}
							disabled={isAccepting}
							aria-label="Accept call"
							aria-busy={isAccepting}
							className="flex h-14 items-center justify-center gap-2 rounded-full bg-green-600 px-5 text-[17px] font-semibold outline-none active:bg-green-700 focus-visible:ring-2 focus-visible:ring-white disabled:opacity-70 landscape:h-12"
						>
							<Phone aria-hidden className="size-5" />
							{isAccepting ? 'Accepting…' : 'Accept'}
						</button>
					</div>
				</div>
			</div>,
			document.body,
		);
	}

	return (
		<div className="fixed top-4 right-4 z-[1000]">
			<div
				role="alert"
				aria-live="assertive"
				className="flex flex-col items-center bg-gradient-to-br from-blue-950 via-blue-900 to-black text-white border border-blue-800/50 rounded-2xl shadow-2xl p-4 w-[280px] shadow-blue-500/30"
			>
				{/* Incoming Call Header */}
				<div className="flex flex-col items-center w-full mb-4">
					<div className="relative">
						<div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-800 border-2 border-blue-600 mb-3">
							<span className="text-lg font-bold text-white">
								{callerInitials}
							</span>
						</div>
						{forwardedFromName && (
							<div className="absolute -bottom-1 -right-1 flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 border-2 border-blue-900">
								<PhoneForwarded className="h-3 w-3 text-white" />
							</div>
						)}
					</div>
					<h3 className="text-lg font-semibold text-white mb-1">
						{customerName || 'Incoming Call'}
					</h3>
					<p className="text-sm text-blue-300">
						{formatPhone(callerNumber)}
					</p>
					{forwardedFromName && (
						<p className="text-xs italic text-purple-300 mt-1">
							Forwarded from {forwardedFromName}
						</p>
					)}
					{menuSelection && (
						<p className="mt-2 rounded-full bg-blue-800/70 px-2.5 py-0.5 text-xs font-medium text-blue-100">
							Chose {menuSelection}
						</p>
					)}
				</div>

				{/* Accept/Decline Buttons */}
				<div className="flex items-center gap-3 w-full">
					{/* Decline Button */}
					<Button
						variant="destructive"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							onDecline();
						}}
						className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12 rounded-xl shadow-lg shadow-red-600/30"
						aria-label="Decline call"
					>
						<PhoneOff className="h-5 w-5 mr-2" />
						Decline
					</Button>

					{/* Accept Button */}
					<Button
						variant="default"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							onAccept();
						}}
						className="flex-1 bg-green-600 hover:bg-green-700 text-white h-12 rounded-xl shadow-lg shadow-green-600/30"
						disabled={isAccepting}
						aria-label="Accept call"
						aria-busy={isAccepting}
					>
						<Phone className="h-5 w-5 mr-2" />
						{isAccepting ? 'Accepting…' : 'Accept'}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default IncomingCallDialog;
